// Веб-вклад модуля `library` — Библиотека (наборы материалов и их состав).
//
// Два пункта навигации: сама Библиотека и «Избранное». Избранное принадлежит
// именно ей, а не `materials`: звезда стоит и на источниках, и на папках, и
// раздел показывает их одним списком. В IA оба пункта в первичной группе
// (Избранное сразу после Библиотеки) — группу выбирает `nav-config.ts`.
//
// `activePrefixes` — существенная часть вклада: карточка источника осталась на
// `/materials/$id`, а деталь папки — на `/collections/$setId`; без них открытая
// карточка гасит подсветку раздела.

import { SECTION_CATEGORY, SECTION_ICONS } from "@/components/navigation/section-icons";
import { materialNotInAnyCourse } from "@/lib/powersync/library-scope-sql";
import type { WebModuleManifest } from "./_contract";

// «Не в курсе» — материал не состоит ни в одном служебном наборе
// kind='course_source', ни напрямую, ни через вложенную папку (глубина 1 —
// как в серверном резолвере состава, BLUEPRINT §5). Предикат ОБЩИЙ с поправкой
// счётчиков самого раздела (`components/library/_pending-deletions.ts`): пока
// он жил только здесь, сайдбар и сегмент-контрол считали разными формулами.
const NOT_IN_COURSE_SQL = `
	SELECT count(*) FROM materials m
	WHERE m.user_id = ?
	  AND m.deleted_at IS NULL
	  AND ${materialNotInAnyCourse("m")}
`;

// Избранное = материалы + папки со звездой. Два параметра `?` — обоим
// подставляется один и тот же `user_id`.
const FAVORITES_SQL = `
	SELECT
		(SELECT count(*) FROM materials WHERE user_id = ? AND deleted_at IS NULL AND favorite = 1)
		+ (SELECT count(*) FROM folders WHERE user_id = ? AND deleted_at IS NULL AND favorite = 1)
`;

export const libraryWebManifest = {
	id: "library",
	icon: SECTION_ICONS.library,
	labelKey: "nav.library",
	category: SECTION_CATEGORY.library,
	// `/library/folders` — деталь папки (канон с 2026-08-14; раньше жила под
	// `/collections/$setId`, то есть список и карточка одной сущности были в
	// разных разделах). `/collections` остаётся: это живой redirect-роут ради
	// deep-link'ов репетитора, уже лежащих в карточках сообщений в БД.
	routes: ["/library", "/library/folders", "/collections", "/favorites"],

	// Баннер РАЗГРАНИЧИВАЕТ Библиотеку с производными разделами: сюда попадают
	// только исходники, а конспекты, тесты и карточки живут в своих. Это самая
	// частая путаница в IA — «где мои материалы» ↔ «где то, что из них собрано».
	intro: {
		titleKey: "library:intro.title",
		bodyKey: "library:intro.body",
		bulletKeys: ["library:intro.bullets.0", "library:intro.bullets.1", "library:intro.bullets.2"],
	},

	nav: [
		{
			key: "library",
			to: "/library",
			icon: SECTION_ICONS.library,
			labelKey: "nav.library",
			activePrefixes: ["/materials", "/collections"],
			order: 40,
			badge: { kind: "powersync", sql: NOT_IN_COURSE_SQL },
		},
		// Избранное принадлежит Библиотеке (звезда на источниках и папках), но в IA
		// стоит в первичной группе сразу после неё — не в «Инструментах».
		{
			key: "favorites",
			to: "/favorites",
			icon: SECTION_ICONS.favorites,
			labelKey: "nav.favorites",
			order: 45,
			badge: { kind: "powersync", sql: FAVORITES_SQL },
		},
	],
} satisfies WebModuleManifest;
